import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/store/auth'

/**
 * The affiliate program (Brian's spec v1.3), minus everything that moves
 * money: commission, netting, payouts and holds wait on payments. The code
 * book, applications, codes and referral attribution are live.
 */

export interface Affiliate {
  user_id: string; client_id: string; code: string; status: 'active' | 'suspended' | 'ended'
  commission_rate: number; book_level: number; slots_available: number; slots_frozen: number; slots_spent: number
  early_churns: number; last_conversion_at: string | null; approved_at: string
}
export interface Application { id: string; status: 'pending' | 'approved' | 'declined'; decline_reason: string | null; reviewed_at: string | null; created_at: string; requested_code: string | null }
export interface ReferralCode { id: string; code: string; discount_pct: number; label: string | null; active: boolean; created_at: string }
export interface Referral { id: string; state: 'first' | 'paying' | 'paused' | 'churned'; joined_at: string; confirmed_at: string | null; cycles_done: number; code_id: string | null }
export interface AffEvent { id: string; kind: string; delta: number; reason: string; created_at: string }

export function useAffiliateState() {
  const userId = useAuthStore((s) => s.user?.id)
  return useQuery({
    queryKey: ['affiliate', userId],
    enabled: !!userId,
    queryFn: async () => {
      const [{ data: aff, error: e1 }, { data: apps, error: e2 }] = await Promise.all([
        supabase.rpc('my_affiliate'),
        supabase.from('affiliate_applications').select('id, status, decline_reason, reviewed_at, created_at, requested_code')
          .eq('user_id', userId!).order('created_at', { ascending: false }).limit(1),
      ])
      if (e1) throw e1
      if (e2) throw e2
      const affiliate = ((aff ?? []) as Affiliate[])[0] ?? null
      if (!affiliate) return { affiliate: null, application: ((apps ?? []) as Application[])[0] ?? null, codes: [], referrals: [], events: [] }
      const [codes, refs, events] = await Promise.all([
        supabase.from('referral_codes').select('*').eq('affiliate_id', userId!).order('created_at'),
        supabase.from('referrals').select('id, state, joined_at, confirmed_at, cycles_done, code_id').eq('affiliate_id', userId!).order('joined_at', { ascending: false }),
        supabase.from('affiliate_events').select('*').eq('affiliate_id', userId!).order('created_at', { ascending: false }).limit(30),
      ])
      return {
        affiliate,
        application: ((apps ?? []) as Application[])[0] ?? null,
        codes: (codes.data ?? []) as ReferralCode[],
        referrals: (refs.data ?? []) as Referral[],
        events: (events.data ?? []) as AffEvent[],
      }
    },
  })
}

export function useApply() {
  const userId = useAuthStore((s) => s.user?.id)
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (a: { channels: string[]; size: string; url: string; audience: string; approach: string; code: string; agreements: Record<string, string> }) => {
      const { error } = await supabase.rpc('apply_affiliate', {
        p_channels: a.channels, p_audience_size: a.size, p_profile_url: a.url,
        p_audience_desc: a.audience, p_approach_desc: a.approach, p_requested_code: a.code, p_agreements: a.agreements,
      })
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['affiliate', userId] }),
  })
}

export function useIssueCode() {
  const userId = useAuthStore((s) => s.user?.id)
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (a: { code: string; discount: number; label: string }) => {
      const { error } = await supabase.rpc('issue_referral_code', { p_code: a.code, p_discount: a.discount, p_label: a.label || null })
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['affiliate', userId] }),
  })
}

export function useSetCodeActive() {
  const userId = useAuthStore((s) => s.user?.id)
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (a: { id: string; active: boolean }) => {
      const { error } = await supabase.rpc('set_referral_code_active', { p_code_id: a.id, p_active: a.active })
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['affiliate', userId] }),
  })
}

// ---------------- Admin review ----------------

export interface PendingApplication extends Application {
  user_id: string; client_id: string; channels: string[]; audience_size: string | null; profile_url: string | null
  audience_desc: string | null; approach_desc: string | null; agreements: Record<string, string>
  users?: { email: string; full_name: string | null } | null
  clients?: { display_name: string; city_state: string; plan_id: string | null } | null
}

export function useApplications(status: 'pending' | 'approved' | 'declined' = 'pending') {
  return useQuery({
    queryKey: ['admin-affiliate-apps', status],
    queryFn: async () => {
      const { data, error } = await supabase.from('affiliate_applications')
        .select('*, users!affiliate_applications_user_id_fkey(email, full_name), clients(display_name, city_state, plan_id)')
        .eq('status', status).order('created_at', { ascending: true })
      if (error) throw error
      return data as PendingApplication[]
    },
  })
}

export function useReview() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (a: { id: string; approve: boolean; reason?: string }) => {
      const { error } = await supabase.rpc('review_affiliate', { p_application_id: a.id, p_approve: a.approve, p_reason: a.reason ?? null })
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-affiliate-apps'] }),
  })
}

// ---------------- The code book, as a model ----------------

/** Brian's simulator, matching the database's book rules (§1.18). */
export function simulateBook(stuck: number, churned: number, quiet: number) {
  const START = 5, CEIL = 32
  let book = START, avail = START, spent = 0, early = 0
  const log: { delta: string; reason: string; to: number }[] = []
  // Interleave, so order effects are honest rather than run in blocks.
  const events: ('stuck' | 'churn')[] = []
  const total = stuck + churned
  let s = 0, c = 0
  for (let i = 0; i < total; i++) {
    const wantStuck = stuck > 0 && (c >= churned || (s + 1) / stuck <= (c + 1) / Math.max(churned, 1))
    if (wantStuck && s < stuck) { events.push('stuck'); s++ } else { events.push('churn'); c++ }
  }
  for (const e of events) {
    if (e === 'stuck') {
      if (book >= CEIL) { log.push({ delta: '±0', reason: 'Confirmed. At the ceiling, the slot comes straight back.', to: book }); continue }
      avail -= 1; spent += 1
      if (avail <= 0) {
        book = Math.min(CEIL, book * 2); avail = book; spent = 0
        log.push({ delta: `×2`, reason: `Book worked through. It doubles to ${book}.`, to: book })
      } else log.push({ delta: '−1', reason: 'Confirmed. One slot spent toward the next doubling.', to: book })
    } else {
      if (spent > 0) { spent -= 1; avail += 1 }
      if (early === 1) {
        early = 0
        if (book > 1) { book -= 1; avail = Math.max(1, Math.min(avail, book - spent)) }
        log.push({ delta: '−1', reason: 'Cancelled inside 90 days, the second one. The slot returns and the book drops by 1.', to: book })
      } else {
        early = 1
        log.push({ delta: '+1', reason: 'Cancelled inside 90 days. The slot returns without doubling.', to: book })
      }
    }
  }
  const decay = Math.max(0, quiet - 3)
  for (let i = 0; i < decay && book > 1; i++) {
    book -= 1; avail = Math.max(1, avail - 1)
    log.push({ delta: '−1', reason: 'A quiet month past the 3-month grace. The book shrinks by one.', to: book })
  }
  const verdict = book > START ? `Growing: ${book} codes, up from ${START}.`
    : book === START ? 'Holding steady at 5.'
    : book === 1 ? 'At the floor of 1. There is always a way back up.'
    : `Circling: ${book} codes. Churn returns slots but never doubles them.`
  return { book, log, verdict, start: START }
}
